/**
 * Client half of the helper pass (server half: server/search/search-helper-pass.ts).
 *
 * The search bar parks its paid helper calls (/api/check-region-needed)
 * here instead of firing them beside /api/product-search. When product-search answers, `admit` either starts
 * them with the fresh pass (the turn cleared BotID) or cancels them (a 403,
 * a 429, a 5xx). A refused session therefore makes zero helper calls.
 *
 * Dependency-free and framework-free so it can be unit tested without
 * rendering the search bar.
 */
import { SEARCH_HELPER_PASS_HEADER } from '@/lib/botid-routes';

export type ParkedSearchHelpers = {
  /** Fire the helper calls. Runs at most once. */
  start: () => void;
  /** Settle anything awaiting them. */
  cancel: () => void;
};

export type SearchHelperGate = {
  /** Park helpers until the next product-search answer; cancels any still parked. */
  park: (helpers: ParkedSearchHelpers) => void;
  /** A product-search answer arrived: start or cancel whatever is parked. */
  admit: (response: { ok: boolean; headers: { get: (name: string) => string | null } }) => void;
  /** Drop whatever is parked (new search, reset). */
  cancel: () => void;
  /**
   * Keep a pass another admitted call minted (/api/link-preview after its own
   * BotID verdict), without starting or cancelling anything parked.
   */
  remember: (headers: { get: (name: string) => string | null }) => void;
  /** Request headers for a helper call: JSON, plus the latest pass if any. */
  headers: () => Record<string, string>;
};

export function createSearchHelperGate(
  opts: {
    /** A fresh pass arrived: calls refused for want of one can ask again (link previews). */
    onPass?: (headers: Record<string, string>) => void;
  } = {},
): SearchHelperGate {
  let parked: ParkedSearchHelpers | null = null;
  let pass: string | null = null;

  const headers = (): Record<string, string> => ({
    'Content-Type': 'application/json',
    ...(pass ? { [SEARCH_HELPER_PASS_HEADER]: pass } : {}),
  });

  const take = (): ParkedSearchHelpers | null => {
    const current = parked;
    parked = null;
    return current;
  };

  return {
    park(helpers) {
      take()?.cancel();
      parked = helpers;
    },
    admit(response) {
      const current = take();
      if (!response.ok) {
        current?.cancel();
        return;
      }
      const fresh = response.headers.get(SEARCH_HELPER_PASS_HEADER);
      if (fresh) pass = fresh;
      current?.start();
      if (fresh) opts.onPass?.(headers());
    },
    cancel() {
      take()?.cancel();
    },
    remember(headers) {
      const fresh = headers.get(SEARCH_HELPER_PASS_HEADER);
      if (fresh) pass = fresh;
    },
    headers,
  };
}
